import React from "react";
import { SelectMenuItem } from "evergreen-ui";
import { CostMetadata, ShopifyProductVariant } from "../../../../../../common/models";
import { ColumnConfig } from "../../../../common/tables/config/ColumnConfig";
import { EnumResolver } from "../../../../common/tables/etc/EnumResolver";
import {
    TableCellChangedHandler,
    TableCellFactory,
} from "../../../../common/tables/factory";
import { CostMetadataTableRow } from "../../../../../../common/tables/cost-metadata/CostMetadataTableRow";
import { CostMetadataEnumMapper } from "../etc/CostMetadataEnumMapper";
import { CostMetadataWithRelations } from "../../CostMetadataTab";

import { CostPayoutModifier } from "../../../../../../common/models/CostPayoutModifier";
import { SupportedColor } from "../../../../common/tables/etc/StyledSelectMenuItem";

export interface CostMetadataTableCellCallbacks {
    onVariantsCellClick?: (rowData: CostMetadata) => void;
    variantFilterOptions?: ShopifyProductVariant[];
    costPayoutModifiers?: CostPayoutModifier[];
}

const DOLLAR_COLUMNS: Set<string> = new Set([
    "unitCost", "juniperServiceFee", "fulfillmentFee", "shippingSubsidy",
]);
const PERCENT_COLUMNS: Set<string> = new Set(["processingFeePercent"]);

export class CostMetadataTableCellFactory extends TableCellFactory<CostMetadataTableRow, CostMetadata> {
    readonly enumResolver: EnumResolver<CostMetadataTableRow> = new CostMetadataEnumMapper();
    private callbacks: CostMetadataTableCellCallbacks;

    constructor(callbacks: CostMetadataTableCellCallbacks = {}) {
        super();
        this.callbacks = callbacks;
    }

    public resolveFilterOptions(column: ColumnConfig<CostMetadataTableRow>): SelectMenuItem[] {
        if (column.columnName === "variantSkus" && this.callbacks.variantFilterOptions) {
            return this.callbacks.variantFilterOptions.map((v) => ({
                label: v.sku || v.title || String(v.id),
                value: String(v.id),
            }));
        }
        if ((column.columnName as string) === "costPayoutModifierId" && this.callbacks.costPayoutModifiers) {
            return [
                { label: "None", value: "" },
                ...this.callbacks.costPayoutModifiers.map(m => ({ label: m.name, value: m.id })),
            ];
        }
        return super.resolveFilterOptions(column);
    }

    // Override required: base class calls enumResolver.resolveEnum(...).map() which
    // fails because CostMetadataEnumMapper returns an EnumMapper object, not an array.
    protected resolveCellBadgeColor(
        _columnConfig: ColumnConfig<CostMetadataTableRow>,
        _rowData: CostMetadata,
    ): SupportedColor {
        return undefined;
    }

    public renderBodyCell(
        columnConfig: ColumnConfig<CostMetadataTableRow>,
        rowData: CostMetadata,
        onCellChanged: TableCellChangedHandler<CostMetadataTableRow>,
    ): JSX.Element {
        if (columnConfig.columnName === "variantSkus") {
            const variants = (rowData as CostMetadataWithRelations).shopifyProductVariant ?? [];
            if (variants.length > 0) {
                const first = variants[0].sku || variants[0].title || String(variants[0].id);
                const children: React.ReactNode[] = [first];
                if (variants.length > 1) {
                    children.push(`, +${variants.length - 1}`);
                }
                return React.createElement(
                    "span",
                    { className: "jc-cell-clickable", onClick: () => this.callbacks.onVariantsCellClick?.(rowData) },
                    ...children,
                );
            }
            return React.createElement(
                "span",
                { className: "jc-cell-actionable", onClick: () => this.callbacks.onVariantsCellClick?.(rowData) },
                React.createElement("span", { className: "jc-cell-hover-text" }, "Click to assign"),
            );
        }

        return super.renderBodyCell(columnConfig, rowData, onCellChanged);
    }

    protected resolveCellData(
        columnConfig: ColumnConfig<CostMetadataTableRow>,
        rowData: CostMetadata,
    ): string {
        const data = rowData as CostMetadataWithRelations;
        const colName = columnConfig.columnName as string;

        if (colName === "variantSkus") {
            const variants = data.shopifyProductVariant ?? [];
            if (variants.length === 0) {
                return "";
            }
            const first = variants[0].sku || variants[0].title || String(variants[0].id);
            if (variants.length === 1) {
                return first;
            }
            return `${first}, +${variants.length - 1}`;
        }

        if (colName === "costPayoutModifierName") {
            return data.costPayoutModifier?.name ?? "";
        }

        if (colName === "costPayoutModifierId") {
            return data.costPayoutModifier?.name ?? data.costPayoutModifierId ?? "";
        }

        if (DOLLAR_COLUMNS.has(colName) || PERCENT_COLUMNS.has(colName)) {
            const raw = (data as unknown as Record<string, unknown>)[colName];
            const num = Number(raw);
            if (isNaN(num)) {
                return String(raw ?? "");
            }
            return num.toFixed(2);
        }

        return super.resolveCellData(columnConfig, rowData);
    }
}
